import { MAIN_CONTENT_ID } from '@shared/constants/ContentIdValues';
import ISearchParams from '@shared/models/ISearchParams';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useAlertsStore } from './state/AlertsContainer';
import { useOrganizationStore } from './state/OrganizationContainer';
import useScrollToElement from './useScrollToElement';
import { DEBOUNCE_MS } from '@shared/constants/Debounce';

export default function useOrgProductsSearch() {
    const { t, i18n } = useTranslation();
    const { organizationId } = useParams<{ organizationId: string }>();
    const orgProducts = useOrganizationStore((state) => state.orgProducts);
    const defaultTake = 51;
    const displayLimit = defaultTake - 1;
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [debouncedQuery, setDebouncedQuery] = useState<ISearchParams>({
        searchText: '',
        skip: 0,
        take: defaultTake,
    });

    useScrollToElement(MAIN_CONTENT_ID, [debouncedQuery.skip]);

    function handleSearchQueryChange(value: string) {
        setSearchQuery(value);
    }

    const showPrevious = useMemo(() => debouncedQuery.skip && debouncedQuery.skip > 0, [debouncedQuery.skip]);
    const showNext = useMemo(() => orgProducts.length >= displayLimit, [orgProducts]);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            const newQuery = {
                searchText: searchQuery,
                skip: 0,
                take: defaultTake,
            };
            if (newQuery.searchText !== debouncedQuery.searchText) {
                setDebouncedQuery(newQuery);
            }
        }, DEBOUNCE_MS);

        return () => clearTimeout(timeoutId);
    }, [searchQuery]);

    const handleNext = () => {
        const count = useOrganizationStore.getState().orgManagedLicenseKeys.length;
        setDebouncedQuery((prev) => ({
            ...prev,
            take: defaultTake,
            skip: displayLimit + (prev.skip ?? 0),
        }));
    };

    const handlePrevious = () => {
        const count = orgProducts.length;
        setDebouncedQuery((prev) => ({
            ...prev,
            take: defaultTake,
            skip: Math.max(0, (prev.skip ?? 0) - displayLimit),
        }));
    };

    useEffect(() => {
        const trySearch = async () => {
            if (useOrganizationStore.getState().loadingData.products || !organizationId) return;
            try {
                useOrganizationStore.getState().handleLoadingDataChange('products', true);
                useOrganizationStore.getState().getOrganizationProducts(organizationId, i18n.language, debouncedQuery);
            } catch (e) {
                useAlertsStore.getState().showError(t('common:license.key.product.error.title'));
            } finally {
                useOrganizationStore.getState().handleLoadingDataChange('products', false);
            }
        };
        trySearch();
    }, [debouncedQuery]);

    return {
        results: orgProducts,
        handleSearchQueryChange,
        searchQuery,
        showPrevious,
        showNext,
        handleNext,
        handlePrevious,
        loadingData: useOrganizationStore.getState().loadingData.licenseKeys,
        displayLimit,
    };
}
